import { useEffect } from "react";

interface SEOOptions {
  title: string;
  description?: string;
  ogImage?: string;
  /** 'article' for news posts, 'room' for accommodation, default for general pages */
  type?: "article" | "room" | "page";
  /** For articles: publish date ISO string */
  publishedAt?: string;
  /** For articles: author name */
  author?: string;
  /** For rooms: price string e.g. "IDR 800.000 / Night" */
  price?: string;
  /** For rooms: room size e.g. "50 sqm" */
  roomSize?: string;
}

const SITE_URL = "https://patalkikianbali.com";
const BRAND = "Patal Kikian Villas";

function setMetaTag(name: string, content: string, isProperty = true) {
  const key = isProperty ? "property" : "name";
  let meta = document.querySelector(`meta[${key}="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(key, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function setJsonLd(id: string, data: object) {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function setHreflangTags() {
  const supportedLangs = ['en', 'id', 'ar', 'zh-CN', 'nl', 'fr', 'de', 'it', 'pt', 'ru', 'es'];
  
  // Custom mapping for ID region as requested
  const hreflangMap: Record<string, string> = {
    'en': 'en-ID',
    'id': 'id-ID',
    'ar': 'ar-ID',
    'zh-CN': 'zh-ID',
    'nl': 'nl-ID',
    'fr': 'fr-ID',
    'de': 'de-ID',
    'it': 'it-ID',
    'pt': 'pt-ID',
    'ru': 'ru-ID',
    'es': 'es-ID',
  };

  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);
  const isFirstSegmentLang = supportedLangs.includes(segments[0]);
  
  if (isFirstSegmentLang) {
    segments.shift(); 
  }
  const cleanPath = segments.length > 0 ? '/' + segments.join('/') : '';

  // Remove existing alternate links
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

  // Add x-default
  let link = document.createElement("link");
  link.setAttribute("rel", "alternate");
  link.setAttribute("hreflang", "x-default");
  link.setAttribute("href", `${SITE_URL}${cleanPath}`);
  document.head.appendChild(link);

  supportedLangs.forEach(lang => {
    let link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflangMap[lang] || lang);
    const langPrefix = lang === 'en' ? '' : `/${lang}`;
    link.setAttribute("href", `${SITE_URL}${langPrefix}${cleanPath}`);
    document.head.appendChild(link);
  });
}

export function useSEO(
  title: string,
  customDescription?: string,
  ogImage?: string,
  options?: Partial<Omit<SEOOptions, "title" | "description" | "ogImage">>
) {
  useEffect(() => {
    const isHome = !title || title.toLowerCase() === "home";

    // 1. Title
    const pageTitle = isHome
      ? `${BRAND} Sidemen - Awaken to Bali's Most Breathtaking Panorama`
      : `${title} - ${BRAND} Sidemen`;
    document.title = pageTitle;

    // Set Hreflang Tags
    setHreflangTags();

    // 2. Description
    const baseDesc =
      "Embrace the majestic presence of Mount Agung as it watches over emerald rice terraces in Sidemen, Bali. A luxury sanctuary where crisp mountain air and untouched serenity create an unforgettable escape at Patal Kikian Villas.";
    const description =
      customDescription ||
      (isHome ? baseDesc : `${title} at ${BRAND} – ${baseDesc}`);

    setMetaTag("description", description, false);

    // 3. Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", SITE_URL + window.location.pathname);

    // 4. Robots
    setMetaTag("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1", false);

    // 5. Open Graph
    const imageUrl = ogImage
      ? ogImage.startsWith("http")
        ? ogImage
        : SITE_URL + ogImage
      : `${SITE_URL}/media/patal-kikian-hero-banner.webp`;

    setMetaTag("og:type", options?.type === "article" ? "article" : "website");
    setMetaTag("og:title", pageTitle);
    setMetaTag("og:description", description);
    setMetaTag("og:url", SITE_URL + window.location.pathname);
    setMetaTag("og:image", imageUrl);
    setMetaTag("og:image:width", "1920");
    setMetaTag("og:image:height", "1080");
    setMetaTag("og:site_name", BRAND);
    setMetaTag("og:locale", "en_US");

    if (options?.type === "article" && options.publishedAt) {
      setMetaTag("article:published_time", options.publishedAt);
      setMetaTag("article:author", options.author || BRAND);
    }

    // 6. Twitter Card
    setMetaTag("twitter:card", "summary_large_image", false);
    setMetaTag("twitter:title", pageTitle, false);
    setMetaTag("twitter:description", description, false);
    setMetaTag("twitter:image", imageUrl, false);

    // 7. JSON-LD Structured Data
    if (options?.type === "article") {
      setJsonLd("ld-article", {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: title,
        description: description,
        image: imageUrl,
        author: {
          "@type": "Organization",
          name: options.author || BRAND,
        },
        publisher: {
          "@type": "Organization",
          name: BRAND,
          logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.webp` },
        },
        datePublished: options.publishedAt || new Date().toISOString(),
        dateModified: new Date().toISOString(),
        mainEntityOfPage: { "@type": "WebPage", "@id": SITE_URL + window.location.pathname },
      });
    } else if (options?.type === "room") {
      setJsonLd("ld-room", {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        name: `${title} - ${BRAND}`,
        description: description,
        image: imageUrl,
        url: SITE_URL + window.location.pathname,
        priceRange: options.price || "IDR 800.000+",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Sidemen",
          addressRegion: "Bali",
          addressCountry: "ID",
        },
        containedInPlace: {
          "@type": "Hotel",
          name: BRAND,
          url: SITE_URL,
        },
      });
    } else if (isHome) {
      setJsonLd("ld-hotel", {
        "@context": "https://schema.org",
        "@type": "Hotel",
        name: BRAND,
        url: SITE_URL,
        description: description,
        image: imageUrl,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Banjar Kikian, Sidemen",
          addressLocality: "Karangasem",
          addressRegion: "Bali",
          postalCode: "80864",
          addressCountry: "ID",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: -8.4378,
          longitude: 115.4503,
        },
        telephone: "+62 813-5327-8504",
        email: "info@patalkikianbali.com",
        priceRange: "IDR 800.000 - IDR 2.500.000",
        starRating: { "@type": "Rating", ratingValue: "5" },
        amenityFeature: [
          { "@type": "LocationFeatureSpecification", name: "Infinity Pool", value: true },
          { "@type": "LocationFeatureSpecification", name: "Mountain View", value: true },
          { "@type": "LocationFeatureSpecification", name: "Free Breakfast", value: true },
          { "@type": "LocationFeatureSpecification", name: "Spa", value: true },
          { "@type": "LocationFeatureSpecification", name: "Restaurant", value: true },
        ],
      });
    }
  }, [title, customDescription, ogImage, options?.type, options?.publishedAt]);
}
